/* Form-family park: the file cards read their read-only state from it until the upload round (component-map.md). */
import {
  As,
  Flex,
  FlexProps,
  Grid,
  GridItem as ChakraGridItem,
  GridItem,
  GridProps,
  useColorModeValue,
  useFormControlContext,
} from '@chakra-ui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { IconButton as CkIconButton } from '@main/complykit/components/icon-button';
import { Stack } from '@main/complykit/components/stack';
import { Status } from '@main/complykit/components/status';
import { Tag } from '@main/complykit/components/tag';
import { Nullable } from '@main/shared/types';
import { isNonNullable } from '@main/shared/utils';
import { ComponentType, ReactNode, SVGProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MenuActions } from '../actions/menu-actions';
import { useOptional } from '../hooks';
import { labelColorFromColorScheme, toneFromColorScheme } from '../legacy-color-scheme';
import { CardContext, useCardContext } from './card-context';
import { TagConfig } from './card-tags';

const cardInteractiveSelector =
  'button, a, input, select, textarea, label, [tabindex], [role="button"], [role="checkbox"], [role="link"], [role="menuitem"]';

interface CardProps extends GridProps {
  size?: 'sm' | 'md';
  isReadOnly?: boolean;
  onPreview?: () => void;
  previewLabel?: string;
}

export const Card = ({
  children,
  size = 'sm',
  isReadOnly: cardReadOnly,
  onPreview,
  previewLabel,
  ...props
}: CardProps) => {
  const borderColor = useColorModeValue('gray.50', 'gray.600');
  /**
   *  We are using Grid Areas to place specific parts of the card in certain positions.
   *  The Areas are defined as follows:
   *  - left: The left are placed in the top left corner.
   *  - right: The right of the card are placed in the top right corner.
   *  - main:
   *    - For small cards, the main is placed in center, between left and right.
   *    - For large cards, the main is moved to the second row and takes up full length of the card.
   */
  const templateAreas = size === 'sm' ? `"left main right"` : `"left . right" "main main main"`;
  const formContext = useOptional(useFormControlContext);
  const isReadOnly = cardReadOnly ?? formContext?.isReadOnly;

  return (
    <CardContext.Provider
      value={{
        size,
        isReadOnly: !!isReadOnly,
      }}
    >
      <Grid
        templateAreas={templateAreas}
        gridTemplateColumns="auto 1fr auto"
        w="full"
        px={3}
        py={2}
        gap={3}
        rounded="lg"
        shadow="sm"
        border="1px"
        alignItems="center"
        borderColor={borderColor}
        position={onPreview ? 'relative' : undefined}
        cursor={onPreview ? 'pointer' : undefined}
        _hover={onPreview ? { borderColor: 'purple.300', shadow: 'md' } : undefined}
        sx={
          onPreview
            ? {
                '& > [data-card-region]': { position: 'relative' },
              }
            : undefined
        }
        onClick={
          onPreview
            ? (event) => {
                if (
                  event.target instanceof Element &&
                  !event.currentTarget.contains(event.target.closest(cardInteractiveSelector))
                ) {
                  onPreview();
                }
              }
            : undefined
        }
        {...props}
      >
        {onPreview && (
          // Compound attachment park: the upload round retires the sibling preview target.
          <ChakraGridItem
            as="button"
            type="button"
            onClick={onPreview}
            aria-label={previewLabel}
            position="absolute"
            inset={0}
            w="full"
            h="full"
            p={0}
            border={0}
            bg="transparent"
            rounded="inherit"
            _focusVisible={{
              borderRadius: 'calc(var(--chakra-radii-lg) - 1px)',
              outline: '2px solid',
              outlineColor: 'purple.400',
              outlineOffset: '3px',
            }}
          />
        )}
        {children}
      </Grid>
    </CardContext.Provider>
  );
};

interface CardBodyProps extends FlexProps {
  children: ReactNode;
}
const CardBody = ({ children, ...props }: CardBodyProps) => {
  return (
    <GridItem area="main" data-card-region>
      <Flex gap={2} alignItems="center" w="full" flexGrow={1} {...props}>
        {children}
      </Flex>
    </GridItem>
  );
};

interface CardHeaderProps extends Partial<CardActionsProps> {
  right?: ReactNode;
  left?: ReactNode;
}

const CardHeader = (props: CardHeaderProps) => {
  return (
    <>
      <GridItem area="left" data-card-region>
        {props.left}
      </GridItem>
      <GridItem area="right" as={Flex} gap={2} alignItems="center" data-card-region>
        {props.right}
        <Card.Actions actions={props.actions} numberOfInlineActions={props.numberOfInlineActions} />
      </GridItem>
    </>
  );
};

export interface CardTagsProps extends FlexProps {
  tags?: (TagConfig | undefined)[];
}
const CardTags = ({ tags, ...flexProps }: CardTagsProps) => {
  if (!tags) return null;
  const resolvedTags = tags.filter(isNonNullable);
  return (
    <Flex gap={2} alignItems="center" {...flexProps}>
      {resolvedTags.map((tag) => {
        if (tag.type === 'status') {
          return (
            <Status key={tag.label} tone={tag.tone ?? toneFromColorScheme(tag.colorSchema)}>
              {tag.label}
            </Status>
          );
        }

        return (
          <Tag key={tag.label} color={labelColorFromColorScheme(tag.colorSchema)}>
            {tag.label}
          </Tag>
        );
      })}{' '}
    </Flex>
  );
};

export interface CardAction {
  label: string;
  icon: As;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string;
  /**
   * Mark this action as read action.
   * Will be rendered even in read-only mode.
   * Non-read actions will not be rendered in read-only mode.
   */
  isRead?: boolean;
}
export interface CardActionsProps {
  actions?: Nullable<CardAction>[];
  numberOfInlineActions?: number;
}
const CardActions = ({ actions, numberOfInlineActions = 3 }: CardActionsProps) => {
  const { t } = useTranslation('ui');
  const { isReadOnly } = useCardContext();

  const { inlineActions, menuActions } = useMemo(() => {
    const validActions = actions
      ?.filter(isNonNullable)
      .filter((action) => !isReadOnly || action.isRead);
    const inlineActions = validActions?.slice(0, numberOfInlineActions) ?? [];
    const menuActions = validActions?.slice(numberOfInlineActions) ?? [];

    return { inlineActions, menuActions };
  }, [actions, isReadOnly, numberOfInlineActions]);

  if (!inlineActions.length && !menuActions.length) {
    return null;
  }

  // No gap: the 32px squares carry their own inset around the glyph, so
  // added space between them reads double-wide.
  return (
    <Stack direction="row" gap={0} align="center">
      {/* No tooltip wrapper: the label IS the tooltip, so IconButton's
          built-in pairing carries it. */}
      {inlineActions.map((action) => (
        <CkIconButton
          key={action.label}
          icon={action.icon as ComponentType<SVGProps<SVGSVGElement>>}
          label={action.label}
          isPending={action.isLoading}
          isDisabled={action.isDisabled}
          tooltip={action.isDisabled ? action.disabledTooltip : undefined}
          onPress={() => {
            if (!action.isDisabled) action.onClick();
          }}
        />
      ))}

      {menuActions.length ? (
        <MenuActions
          actions={menuActions.map((action) => ({
            ...action,
            isDisabled: action.isDisabled ?? false,
            disabledTooltip: action.disabledTooltip ?? action.label,
          }))}
          placement="bottom-end"
          trigger={
            <CkIconButton
              icon={EllipsisVerticalIcon}
              label={t('drawer.actions.more')}
              isPending={menuActions.some((action) => action.isLoading)}
            />
          }
        />
      ) : null}
    </Stack>
  );
};

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Actions = CardActions;
Card.Tags = CardTags;
